import {type FC, useEffect, useRef, useState} from 'react';

import {useI18n} from '@yourcoach/i18n';

import {getWaitingRoomBirdAnimation} from '../../../../assets/animations/waiting-room-bird';
import {Icon} from '../../../../uikit/Icon';
import {useWindowDimensions} from '../../../../utils/hooks/useWindowDimensions';
import {FACT_CHANGE_INTERVAL_IN_SECONDS} from './facts';
import * as S from './styles';
import {getRandomFact} from './utils';

export const ConferenceWaitingRoom: FC<ConferenceWaitingRoom.Props> = ({
  description,
  icon,
  shouldShowFact = true,
  title,
}) => {
  const {t} = useI18n();

  const {height: wh, width: ww} = useWindowDimensions();

  const [randomFactKey, setRandomFactKey] = useState(getRandomFact());

  const factChangeInterval = useRef(0);

  useEffect(() => {
    factChangeInterval.current = +setInterval(() => {
      setRandomFactKey(getRandomFact());
    }, FACT_CHANGE_INTERVAL_IN_SECONDS * 1000);

    return () => {
      clearInterval(factChangeInterval.current);
    };
  }, []);

  const isSmallUI = ww >= 320 && ww < 950;
  const isShortUI = wh <= 667;

  return (
    <S.Wrapper>
      <S.Container>
        {icon ? (
          <Icon color="icon.gray" size={isShortUI ? 100 : 160} svg={icon} />
        ) : (
          <S.Icon
            autoPlay={true}
            colorify={{color: 'icon.gray'}}
            isShortUI={isShortUI}
            keepLastFrame={true}
            loop
            source={getWaitingRoomBirdAnimation}
            speed={1}
          />
        )}
        <S.TitleContainer>
          {title ? (
            <S.Title isSmallUI={isSmallUI}>{title}</S.Title>
          ) : (
            <>
              <S.Title isSmallUI={isSmallUI}>
                {t('shared:hey_there_early_bird')}
              </S.Title>
              <S.Title isSmallUI={isSmallUI}>
                {t('shared:welcome_to_pre_session_party')}
              </S.Title>
            </>
          )}
        </S.TitleContainer>
        <S.Subtitle isSmallUI={isSmallUI}>
          {description || t('shared:conference_awaiting_coach')}
        </S.Subtitle>
        {shouldShowFact ? (
          <>
            <S.DidYouKnow>
              {t('shared:in_the_meantime_did_you_know')}
            </S.DidYouKnow>
            <S.Fact>
              <S.FactText>{t(randomFactKey)}</S.FactText>
            </S.Fact>
          </>
        ) : null}
      </S.Container>
    </S.Wrapper>
  );
};

export namespace ConferenceWaitingRoom {
  export type Props = {
    description?: string;
    icon?: FC;
    shouldShowFact?: boolean;
    title?: string;
  };
}
